// A repository's branches, as its page's Branches tab lists them
// (docs/design.md "Repository page"): each branch as the listing reads it
// (`REPO_BRANCH_LIST`: its name and tip) and, once read, its details
// (`branchDetailsQuery`: checks, open and merged pull requests, standing
// against the default branch), the group it falls in, and what a row says
// of it. Pure: the store (state/branches.svelte.ts) reads and keeps them.
//
// A branch with an open pull request is that pull request (a branch and
// its pull request are one line of work), whoever pushed it and however
// old. Otherwise a branch is stale when nothing was pushed to it in
// STALE_DAYS, nothing of it is ahead of the default branch (merged by a
// merge commit or a fast-forward, or never committed to), or a merged pull
// request had its tip as its head (merged by a squash or a rebase, which
// leave its own commits ahead); else it is the viewer's when its tip's
// author or committer is the viewer, and someone else's when not. Until a
// branch's details are read it is grouped by its listing alone.
import { sameLogin } from './attention';
import type { ChecksState } from './checks';
import type { Turn } from './stackRail';
import { repoIndexPath, repoPath } from '../router';

/** A branch's open pull request, as a row shows it and its reviewed share is measured. */
export interface BranchPull {
  /** Its node ID: the dashboard's rows (and its attention set) are read by it. */
  id: string;
  number: number;
  title: string;
  url: string;
  isDraft: boolean;
  /** GitHub's `reviewDecision`; null when the repository asks for no review. */
  reviewDecision: 'APPROVED' | 'CHANGES_REQUESTED' | 'REVIEW_REQUIRED' | null;
  author: string | null;
  /** Its base branch and that branch's tip: its own changes are measured from there, not from the default branch. */
  baseRef: string;
  baseOid: string;
  /** GitHub's counts over all of its files, past the 300 a comparison lists. */
  changedFiles: number;
  additions: number;
  deletions: number;
}

/** What the details query reads of a branch. */
export interface BranchDetail {
  headline: string;
  checks: ChecksState;
  /** Commits on the branch the default branch lacks, and the other way round; null where GitHub gave no comparison. */
  aheadBy: number | null;
  behindBy: number | null;
  pull: BranchPull | null;
  /** A pull request merged into the default branch had the branch's tip as its head. */
  merged: boolean;
  /** GitHub no longer has the branch (deleted since it was listed): it leaves the list. */
  gone: boolean;
}

/** One branch: its tip as the listing reads it, and its details once read. */
export interface Branch {
  name: string;
  tip: {
    oid: string;
    /** The tip's commit date: the last push, as near as GitHub's GraphQL says it. */
    committedDate: string;
    /** The logins GitHub links to the tip's author and committer; null where it links none. */
    author: string | null;
    committer: string | null;
  };
  detail: BranchDetail | null;
}

/** Days without a push after which a branch is stale. */
export const STALE_DAYS = 90;

/** The groups of the Branches tab, in its order. */
export const BRANCH_GROUPS = ['default', 'yours', 'pulls', 'others', 'stale'] as const;
export type BranchGroup = (typeof BRANCH_GROUPS)[number];

export interface GroupContext {
  defaultBranch: string;
  viewer: string;
  /** Epoch ms. */
  now: number;
}

/** GitHub's `StatusState` of a rollup as the app's checks state (logic/checks.ts). */
export function rollupChecks(state: string | null | undefined): ChecksState {
  switch (state) {
    case 'SUCCESS':
      return 'success';
    case 'FAILURE':
    case 'ERROR':
      return 'failure';
    case 'PENDING':
    case 'EXPECTED':
      return 'pending';
    default:
      return 'none';
  }
}

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj | null => (v && typeof v === 'object' ? (v as Obj) : null);
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const login = (actor: unknown): string | null => str(obj(obj(actor)?.user)?.login);
const nodes = (conn: unknown): unknown[] => (obj(conn)?.nodes as unknown[] | undefined) ?? [];

/** A ref of `REPO_BRANCH_LIST` as a branch with no details; null when its target is not a commit. */
export function readListed(node: unknown): Branch | null {
  const n = obj(node);
  const t = obj(n?.target);
  const name = str(n?.name);
  const oid = str(t?.oid);
  if (!n || !t || !name || !oid || t.__typename !== 'Commit') return null;
  return { name, tip: { oid, committedDate: str(t.committedDate) ?? '', author: login(t.author), committer: login(t.committer) }, detail: null };
}

function readPull(v: unknown): BranchPull | null {
  const p = obj(v);
  const number = num(p?.number);
  const id = str(p?.id);
  if (!p || number === null || !id) return null;
  const decision = str(p.reviewDecision);
  return {
    id,
    number,
    title: str(p.title) ?? '',
    url: str(p.url) ?? '',
    isDraft: p.isDraft === true,
    reviewDecision: decision === 'APPROVED' || decision === 'CHANGES_REQUESTED' || decision === 'REVIEW_REQUIRED' ? decision : null,
    author: str(obj(p.author)?.login),
    baseRef: str(p.baseRefName) ?? '',
    baseOid: str(p.baseRefOid) ?? '',
    changedFiles: num(p.changedFiles) ?? 0,
    additions: num(p.additions) ?? 0,
    deletions: num(p.deletions) ?? 0,
  };
}

/** The details of a branch GitHub no longer has. */
const GONE: BranchDetail = Object.freeze({ headline: '', checks: 'none', aheadBy: null, behindBy: null, pull: null, merged: false, gone: true });

/**
 * A ref of `branchDetailsQuery` as the details of the branch at `tip`: gone
 * when GitHub has no such ref (null, or a ref with no target, as a deleted
 * branch answers). The query compares each ref (the base) with the default
 * branch (the head), so GitHub's `aheadBy` is how far the branch is behind;
 * a query asked without the comparison gives none. Merged means into the
 * default branch (`def`): a stacked branch merged into its parent is not.
 */
export function readDetail(node: unknown, tip: string, def: string): BranchDetail {
  const n = obj(node);
  const t = obj(n?.target);
  if (!n || !t) return GONE;
  const cmp = obj(n.compare);
  return {
    headline: str(t?.messageHeadline) ?? '',
    checks: rollupChecks(str(obj(t?.statusCheckRollup)?.state)),
    aheadBy: num(cmp?.behindBy),
    behindBy: num(cmp?.aheadBy),
    pull: nodes(n.open).map(readPull).find((p) => p !== null) ?? null,
    merged: nodes(n.merged).some((m) => str(obj(m)?.headRefOid) === tip && str(obj(m)?.baseRefName) === def),
    gone: false,
  };
}

/** Whether nothing was pushed to `b` in STALE_DAYS, nothing of it is ahead of the default branch, or it was merged. */
function isStale(b: Branch, now: number): boolean {
  const at = Date.parse(b.tip.committedDate);
  return !!b.detail?.merged || b.detail?.aheadBy === 0 || (Number.isFinite(at) && now - at > STALE_DAYS * 86_400_000);
}

/** The group `b` is listed in (see the file comment). */
export function groupOf(b: Branch, ctx: GroupContext): BranchGroup {
  if (b.name === ctx.defaultBranch) return 'default';
  if (b.detail?.pull) return 'pulls';
  if (isStale(b, ctx.now)) return 'stale';
  const mine = (l: string | null) => l !== null && sameLogin(l, ctx.viewer);
  return mine(b.tip.author) || mine(b.tip.committer) ? 'yours' : 'others';
}

/** Newest push first, then by name: the order the tab lists branches in (GitHub lists refs by name only). */
export function byNewest(a: Branch, b: Branch): number {
  return (Date.parse(b.tip.committedDate) || 0) - (Date.parse(a.tip.committedDate) || 0) || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0);
}

/** The rows as shown when one took focus: each group's names, in order. */
export type HeldRows = readonly { group: BranchGroup; names: readonly string[] }[];

/**
 * `groups` as `held` shows them (the rows when one of them took focus):
 * each held row in the group it was in and at its place there, with its
 * details as they are now; rows not held after them, in their own groups,
 * as they came. A row the viewer is on neither moves as more of the
 * listing arrives nor leaves its group when its details arrive (which
 * would re-create it elsewhere and drop the focus). Null holds nothing.
 */
export function holdOrder(groups: { group: BranchGroup; branches: Branch[] }[], held: HeldRows | null): { group: BranchGroup; branches: Branch[] }[] {
  if (!held) return groups;
  const now = new Map(groups.flatMap((g) => g.branches.map((b) => [b.name, b] as const)));
  const placed = new Set(held.flatMap((g) => g.names));
  return groups.map((g) => {
    const kept = (held.find((h) => h.group === g.group)?.names ?? []).flatMap((n) => (now.has(n) ? [now.get(n)!] : []));
    return { group: g.group, branches: [...kept, ...g.branches.filter((b) => !placed.has(b.name))] };
  });
}

/** Every group in BRANCH_GROUPS order (empty ones too), each newest push first. */
export function groupBranches(list: readonly Branch[], ctx: GroupContext): { group: BranchGroup; branches: Branch[] }[] {
  const by = new Map<BranchGroup, Branch[]>(BRANCH_GROUPS.map((g) => [g, []]));
  for (const b of list) by.get(groupOf(b, ctx))!.push(b);
  return BRANCH_GROUPS.map((group) => ({ group, branches: by.get(group)!.sort(byNewest) }));
}

/** A pull request's review state, as a row names it. */
export function reviewState(p: Pick<BranchPull, 'isDraft' | 'reviewDecision'>): string {
  if (p.isDraft) return 'draft';
  switch (p.reviewDecision) {
    case 'APPROVED':
      return 'approved';
    case 'CHANGES_REQUESTED':
      return 'changes requested';
    case 'REVIEW_REQUIRED':
      return 'review required';
    default:
      return 'no review yet';
  }
}

/**
 * Whose turn a pull request is, in the dashboard's words, from its
 * attention set as the dashboard works it out (`turn`, logic/stackRail.ts,
 * of the members `workOutAttention` gives); null while it is not known.
 */
export function turnText(t: Turn): string | null {
  switch (t.kind) {
    case 'you':
      return 'your turn';
    case 'author':
      return 'waiting on author';
    case 'others':
      return `waiting on ${t.logins.join(', ')}`;
    case 'none':
      return 'waiting on nobody';
    case 'unknown':
      return null;
  }
}

/** A branch name in a URL path: percent-encoded but for its slashes. */
const refPath = (ref: string) => encodeURIComponent(ref).replace(/%2F/gi, '/');

/** GitHub's page that opens a pull request of `head` into `base` (Changeling opens none itself). */
export function openPullUrl(webUrl: string, owner: string, repo: string, base: string, head: string): string {
  return `${webUrl}/${owner}/${repo}/compare/${refPath(base)}...${refPath(head)}?expand=1`;
}

/** The repository index's Branches view (`/gh?view=branches`, as views/RepoIndex.svelte reads it). */
export const INDEX_BRANCHES = repoIndexPath({ view: 'branches' });

/**
 * The dashboard's one quiet line, from how many of the viewer's branches
 * have no pull request in each repository whose branches are loaded
 * (`owner/repo` → count, and whether more branches exist than were read);
 * null when there are none. It leads to that
 * repository's Branches tab, or the index's Branches view for several.
 */
export function branchQuietLine(counts: ReadonlyMap<string, { count: number; partial: boolean }>): { text: string; href: string } | null {
  const repos = [...counts].filter(([, c]) => c.count > 0);
  const total = repos.reduce((t, [, c]) => t + c.count, 0);
  if (!total) return null;
  // A repository with more branches than were read (past the listing's cap, or still listing) has more, maybe of yours.
  const partial = [...counts.values()].some((c) => c.partial);
  const text = `${partial ? 'At least ' : ''}${total} ${total === 1 ? 'branch' : 'branches'} of yours without a pull request`;
  if (repos.length > 1) return { text, href: INDEX_BRANCHES };
  const [name] = repos[0];
  const [owner, repo] = name.split('/');
  return { text: `${text} in ${name}`, href: repoPath(owner, repo, 'branches') };
}
